import React, { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const Contact = () => {
  const [formData, setFormData] = useState({ 
    name: '', email: '', phone: '', subject: '', otherSubject: '', message: ''
  });
  
  const [status, setStatus] = useState('idle');
  const [token, setToken] = useState(null);

  const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
  const SECRET_KEY = process.env.REACT_APP_CONTACT_SECRET_KEY;
  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (status !== 'idle') setStatus('idle');
  };

  const sanitize = (str) => {
    if (!str) return '';
    return str.replace(/<[^>]*>?/gm, '').trim().substring(0, 1000); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setStatus('no-captcha'); return; }
    const wordCount = formData.message.trim().split(/\s+/).filter(w => w.length > 0).length;
    if (wordCount < 25) { setStatus('too-short'); return; }

    setStatus('submitting');
    try {
      const finalSubject = formData.subject === 'Otro' 
        ? sanitize(formData.otherSubject) 
        : sanitize(formData.subject);

      const cleanData = {
        name: sanitize(formData.name),
        email: sanitize(formData.email),
        phone: sanitize(formData.phone),
        subject: finalSubject,
        message: sanitize(formData.message),
        key: SECRET_KEY,
        captcha: token
      };

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(cleanData),
      });

      const result = await response.json();
      if (result.result === 'success') {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', otherSubject: '', message: '' });
        setToken(null);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="pt-20 bg-[#FFFBF0] dark:bg-[#1C1B17] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-yellow-950 dark:text-[#FFD54F] mb-4 uppercase tracking-tighter italic">Contacto</h2>
          <div className="h-1.5 w-24 bg-amber-500 mx-auto rounded-full mb-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* --- INFORMACIÓN DE CONTACTO CORREGIDA --- */}
          <div className="bg-white dark:bg-[#2A2821] p-8 md:p-10 rounded-[2rem] shadow-sm border border-yellow-100 dark:border-[#594400]">
            <h3 className="text-sm font-black text-yellow-900 dark:text-[#FFD54F] mb-8 uppercase tracking-widest border-b border-yellow-100 dark:border-yellow-900/30 pb-4">Nuestros Datos</h3>
            
            <div className="space-y-8 text-yellow-800 dark:text-gray-200 font-bold">
              
              {/* Dirección */}
              <div className="flex items-start gap-4">
                <span className="text-2xl mt-1 text-amber-600">📍</span>
                <div className="flex flex-col">
                  <span className="text-sm uppercase text-amber-600/70 font-black">Ubicación</span>
                  <p className="text-lg">C. José María Mercado 131, Col. Cruz Verde, Zacoalco de Torres, Jal.</p>
                </div>
              </div>

              {/* Teléfono y WhatsApp con redirección */}
              <div className="flex items-start gap-4">
                <span className="text-2xl mt-1 text-amber-600">📞</span>
                <div className="flex flex-col w-full">
                  <span className="text-sm uppercase text-amber-600/70 font-black">Llamadas y WhatsApp</span>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <a href="tel:+523343902273" className="flex items-center gap-2 bg-yellow-50 dark:bg-[#1C1B17] px-4 py-2 rounded-full border border-yellow-200 dark:border-[#594400] hover:bg-yellow-100 transition-colors">
                      <span className="text-xs uppercase">Llamar:</span> +52 33 4390 2273
                    </a>
                    <a href="https://wa.me/523343902273" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-full border border-green-200 dark:border-green-800 hover:bg-green-100 transition-colors text-green-700 dark:text-green-400">
                      <span className="text-lg">💬</span> WhatsApp
                    </a>
                  </div>
                </div>
              </div>

              {/* Correo Electrónico */}
              <div className="flex items-start gap-4">
                <span className="text-2xl mt-1 text-amber-600">✉️</span>
                <div className="flex flex-col">
                  <span className="text-sm uppercase text-amber-600/70 font-black">Correo Electrónico</span>
                  <a href="mailto:productosapicolasartesanales@gmail.com" className="text-lg hover:text-amber-600 transition-colors break-all">
                    productosapicolasartesanales@gmail.com
                  </a>
                </div>
              </div>

              {/* Horarios con letra más grande */}
              <div className="pt-6 border-t border-yellow-100 dark:border-yellow-900/30">
                <p className="text-xs uppercase text-amber-600 mb-3 font-black">Horarios de Atención:</p>
                <div className="space-y-1 bg-yellow-50/50 dark:bg-black/20 p-4 rounded-2xl border border-yellow-100 dark:border-yellow-900/20">
                  <p className="text-xl md:text-2xl font-black text-yellow-950 dark:text-[#FFD54F]">Lun - Sáb: <span className="font-medium text-lg md:text-xl">11:30 - 15:00 | 17:00 - 21:00</span></p>
                  <p className="text-xl md:text-2xl font-black text-yellow-950 dark:text-[#FFD54F]">Dom: <span className="font-medium text-lg md:text-xl text-amber-700 dark:text-amber-500">11:30 - 15:00</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* FORMULARIO */}
          <form onSubmit={handleSubmit} className="bg-white dark:bg-[#2A2821] p-8 rounded-[2.5rem] shadow-xl border-2 border-yellow-100 dark:border-[#594400] space-y-4">
            {status === 'success' && <div className="p-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-2xl text-sm font-bold border border-green-200">¡Mensaje enviado con éxito! 🍯</div>}
            {status === 'error' && <div className="p-4 bg-red-50 text-red-800 rounded-2xl text-sm font-bold border border-red-200">Error en el sistema. Intenta por WhatsApp.</div>}
            {status === 'too-short' && <div className="p-4 bg-amber-50 text-amber-800 rounded-2xl text-sm font-bold border border-amber-200">El mensaje debe tener al menos 25 palabras. 🐝</div>}
            {status === 'no-captcha' && <div className="p-4 bg-yellow-50 text-yellow-800 rounded-2xl text-sm font-bold border border-yellow-200">Completa la verificación de seguridad. 🤖</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Tu Nombre" className="w-full p-4 bg-yellow-50 dark:bg-[#1C1B17] border border-yellow-200 dark:border-[#594400] rounded-2xl outline-none dark:text-white font-bold text-sm" />
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Teléfono" className="w-full p-4 bg-yellow-50 dark:bg-[#1C1B17] border border-yellow-200 dark:border-[#594400] rounded-2xl outline-none dark:text-white font-bold text-sm" />
            </div>

            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Tu Correo" className="w-full p-4 bg-yellow-50 dark:bg-[#1C1B17] border border-yellow-200 dark:border-[#594400] rounded-2xl outline-none dark:text-white font-bold text-sm" />
            
            <select name="subject" value={formData.subject} onChange={handleChange} required className="w-full p-4 bg-yellow-50 dark:bg-[#1C1B17] border border-yellow-200 dark:border-[#594400] rounded-2xl outline-none dark:text-white font-bold text-sm">
              <option value="" disabled>Motivo de contacto...</option>
              <option value="Ventas">🍯 Ventas / Pedidos</option>
              <option value="Cotizaciones">📋 Cotizaciones</option>
              <option value="Consultas">🐝 Consultas</option>
              <option value="Otro">✏️ Otro...</option>
            </select>

            <div className="relative">
              <textarea name="message" value={formData.message} onChange={handleChange} required rows="5" placeholder="Escribe tu mensaje aquí..." className="w-full p-4 bg-yellow-50 dark:bg-[#1C1B17] border border-yellow-200 dark:border-[#594400] rounded-2xl outline-none dark:text-white font-bold resize-none text-sm"></textarea>
              <span className={`absolute bottom-3 right-4 text-[10px] font-black ${formData.message.split(/\s+/).filter(w => w.length > 0).length >= 25 ? 'text-green-500' : 'text-yellow-600'}`}>
                Palabras: {formData.message.split(/\s+/).filter(w => w.length > 0).length} / 25
              </span>
            </div>

            <div className="flex justify-center py-2">
              <Turnstile siteKey={SITE_KEY} onSuccess={(t) => setToken(t)} />
            </div>

            <button type="submit" disabled={status === 'submitting'} className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest text-sm transition-all ${status === 'submitting' ? 'bg-gray-400' : 'bg-amber-500 dark:bg-[#FFD54F] text-white dark:text-[#1C1B17] hover:scale-[1.01]'}`}>
              {status === 'submitting' ? 'Enviando...' : 'Enviar Mensaje'}
            </button>
          </form>
        </div>
      </div>

      {/* 🗺️ MAPA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="w-full h-[450px] md:h-[600px] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white dark:border-[#594400] grayscale-[0.2] hover:grayscale-0 transition-all duration-700">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3743.8452191753518!2d-103.57727822609107!3d20.223751914942913!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8425f50408dcfefd%3A0xe3a1b6787b16d572!2sProductos%20Apicolas%20Artesanales%20de%20Zacoalco%20de%20Torres!5e0!3m2!1ses-419!2smx!4v1776012022533!5m2!1ses-419!2smx"
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación exacta de Productos Apícolas Artesanales"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default Contact;
